/* eslint-disable react/require-default-props */
import React from 'react';
import camelToCapitalize from '@utils/functions/camelToCapitalize';
import snakeToCapitalize from '@utils/functions/snakeToCapitalize';
import { translate } from '@utils/i18n';
import { humanizeInspectableType } from '../../utils/humanizeInspectableType';
import { IssueStatusEnum } from '../../types';
import ManagerNote from './ManagerNote';
import IssueLayout from './IssueLayout';
import DeviceInfoToggle from './DeviceInfoToggle';
import './styles.scss';
export default function OutOfSpecIssueCard({ issue, defaultExpanded, onToggle, onUpdateIssue, onUndoAction, readOnly = false, onDeviceInfoToggle, settings }) {
    var _a, _b, _c, _d, _e, _f, _g;
    const status = issue.status || IssueStatusEnum.Detected;
    const isHighImpact = issue.status !== IssueStatusEnum.Resolved && ((_a = issue.issueData) === null || _a === void 0 ? void 0 : _a.highImpact) === true;
    // Get unit - prefer normalizedUnit from payload, fallback to field name inference
    const getUnit = () => {
        var _a;
        if (issue.issueData.normalizedUnit)
            return issue.issueData.normalizedUnit;
        const fieldName = ((_a = issue.issueData.fieldName) === null || _a === void 0 ? void 0 : _a.toLowerCase()) || '';
        if (fieldName.includes('volt'))
            return 'volts';
        if (fieldName.includes('watt'))
            return 'watts';
        if (fieldName.includes('amphour'))
            return 'Ah';
        if (fieldName.includes('height'))
            return 'ft';
        if (fieldName.includes('pressure'))
            return 'PSI';
        if (fieldName.includes('temperature'))
            return '°F';
        if (fieldName.includes('batteries'))
            return 'batteries';
        return 'PSI';
    };
    const unit = getUnit();
    // Get field name for display - strip unit suffixes like __volts, __watts, __amphours, __clean
    const getFieldNameForDisplay = () => {
        var _a;
        const fieldName = ((_a = issue.issueData) === null || _a === void 0 ? void 0 : _a.fieldName) || '';
        return snakeToCapitalize(fieldName.replace(/__(volts|watts|amphours|clean)$/, ''));
    };
    // Handler functions for issue actions
    const handleSuggestCorrection = () => {
        onUpdateIssue(issue.id, status, true);
    };
    const handleDismissIssue = () => {
        onUpdateIssue(issue.id, IssueStatusEnum.DismissedByBackOffice, false);
    };
    const handleNoteChange = (note) => {
        onUpdateIssue(issue.id, status, issue.markedForSuggestion, note);
    };
    // Create high impact badge if needed
    const highImpactBadge = isHighImpact ? (React.createElement("span", { className: "issue-card__badge issue-card__badge--high-impact" }, translate('high_impact', { namespace: 'features.iim_review.issue_card' }))) : undefined;
    return (React.createElement(IssueLayout, { issueType: "invalid_entry", badge: highImpactBadge, status: status, defaultExpanded: defaultExpanded, onToggle: onToggle, onSuggestCorrection: handleSuggestCorrection, onDismissIssue: handleDismissIssue, onUndoAction: onUndoAction, readOnly: readOnly, statusChanges: issue.statusChanges, markedForSuggestion: issue.markedForSuggestion },
        React.createElement(React.Fragment, null,
            React.createElement("p", null,
                React.createElement("span", null, translate('out_of_spec_description', {
                    namespace: 'features.iim_review.issue_card'
                })),
                React.createElement("hr", { className: "section__split" }),
                React.createElement("b", null,
                    ((_b = issue.question) === null || _b === void 0 ? void 0 : _b.type) && `${camelToCapitalize(issue.question.type)} / `,
                    humanizeInspectableType((_c = issue.inspectable) === null || _c === void 0 ? void 0 : _c.type)),
                React.createElement("br", null),
                React.createElement("span", null,
                    getFieldNameForDisplay(),
                    " on",
                    ' ',
                    ((_d = issue.inspectable) === null || _d === void 0 ? void 0 : _d.data.location) || ((_e = issue.inspectable) === null || _e === void 0 ? void 0 : _e.data.name)),
                React.createElement("br", null),
                React.createElement("br", null),
                React.createElement("span", { className: "issue-card__label" },
                    React.createElement("b", null,
                        translate('out_of_spec_current', { namespace: 'features.iim_review.issue_card' }),
                        ":")),
                React.createElement("br", null),
                React.createElement("div", { className: "issue-card__current-value" },
                    React.createElement("div", { className: "issue-card__current-value-box" },
                        React.createElement("i", { className: "fa-light fa-circle-exclamation" }),
                        React.createElement("span", { className: "issue-card__current-value-text" }, (_f = issue.issueData.parsedValue) !== null && _f !== void 0 ? _f : issue.issueData.payloadValue)),
                    React.createElement("span", { className: "issue-card__current-value-unit" }, unit)),
                React.createElement("br", null),
                React.createElement("br", null),
                React.createElement("span", { className: "issue-card__label" },
                    React.createElement("b", null,
                        translate('out_of_spec_expected_range', { namespace: 'features.iim_review.issue_card' }),
                        ":")),
                React.createElement("br", null),
                React.createElement("span", null,
                    issue.issueData.min,
                    " - ",
                    issue.issueData.max,
                    " ",
                    unit)),
            React.createElement(DeviceInfoToggle, { inspectable: issue.inspectable, onToggle: onDeviceInfoToggle }),
            issue.markedForSuggestion && (!readOnly || ((_g = issue.managerNote) === null || _g === void 0 ? void 0 : _g.trim())) && (React.createElement("div", { className: "issue-card__body" },
                React.createElement(ManagerNote, { note: issue.managerNote || '', onNoteChange: handleNoteChange, disabled: readOnly }))))));
}
